import { useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import PageLayout from "../components/UI/Layout/PageLayout";
import TemplateAuthentication from "../components/UI/TemplateAuthentication";
import Card from "../components/UI/Card";
import Callout from "../components/UI/Callout";
// @ts-expect-error pure js
import { classnames, handleAPIError, TextOneOrMany } from "../utils";
// @ts-expect-error pure js
import { Qalimo, YellowStar } from "../components/UI/Icons";
// @ts-expect-error pure js
import IconAndText from "../components/UI/IconAndText";
// @ts-expect-error pure js
import TextInputStandalone from "../components/UI/TextInputStandalone";
import Button from "../components/UI/Button";
// @ts-expect-error pure js
import useAuthenticatedAxios from "../hooks/use-authenticated-axios";
// @ts-expect-error pure js
import useAccount from "../hooks/use-account";
import style from "./SubscriptionPage.module.css";
// @ts-expect-error pure js
import { useAllStripePrices } from "../hooks/use-stripe-prices";
// @ts-expect-error pure js
import { currencyFormatter } from "../api/Functions";
// @ts-expect-error pure js
import useAuth from "../hooks/use-auth.js";
import Text from "../components/UI/Text";
import Title from "../components/UI/Title";
import Status from "../components/UI/Status";
import Accordion, { AccordionItem } from "../components/UI/Accordion";
import ModalDialog from "../components/UI/ModalDialog";
import SubscriptionCheckout from "../components/Stripe/SubscriptionCheckout";
import {
  getMotivation,
  MOTIVATIONS,
  type Motivation,
} from "../utils/constants/motivations";
import { IconCircleCheckFilled } from "@tabler/icons-react";

const FEATURES = [
  "Assistance juridique et technique illimitée",
  "Synchronisation du compte bancaire",
  "Rappels et relances automatiques",
  "État des lieux numérique avec photos",
  "Régularisation des charges locataires",
  "Révision et indexation annuelle du loyer",
  "Gestion multi-bailleurs (SCI, LMNP, etc)",
  "Contrat de bail rédigé par un huissier",
  "Quittancement automatique",
];

const FAQ_ITEMS = [
  {
    title: "Comment fonctionne l'essai gratuit de 30 jours ?",
    text: "Vous accédez à tout Qalimo pendant 30 jours. Le premier paiement n'intervient qu'à la fin de l'essai, et vous pouvez annuler à tout moment avant, en deux clics.",
  },
  {
    title: "Et si Qalimo ne me convient pas ?",
    text: "L'abonnement est sans engagement : vous résiliez quand vous voulez depuis votre compte. Et vous êtes satisfait ou remboursé pendant 6 mois.",
  },
  {
    title: "Le prix évolue-t-il avec le nombre de biens ?",
    text: "Le tarif dépend du nombre de biens gérés, et votre formule s'ajuste à tout moment si votre patrimoine évolue.",
  },
  {
    title: "L'abonnement est-il déductible de mes impôts ?",
    text: "Oui, l'abonnement est 100 % déductible de vos revenus locatifs, comme frais de gestion.",
  },
];

function SidePanel({ motivation }: { motivation: Motivation }) {
  return (
    <div className={style.sidePanel}>
      <div className={style.sideLogo}>
        <Qalimo size={40} fill="white" />
        <Text as="span" color="white" variant="giga">
          alimo
        </Text>
      </div>
      <Title variant="mega" color="white" className={style.sideTitle}>
        {motivation.heroTitle}
      </Title>
      <Text color="white">{motivation.heroSubtitle}</Text>
      <div className={style.sideCard}>
        {motivation.benefits.map((benefit) => (
          <div key={benefit.title} className={style.sideBenefit}>
            <div className={style.sideBenefitIcon}>
              <benefit.icon size={24} />
            </div>
            <div>
              <Text color="white" weight="bold">
                {benefit.title}
              </Text>
              <Text color="white">{benefit.text}</Text>
            </div>
          </div>
        ))}
      </div>
      <div className={style.sideProof}>
        <div className={style.stars}>
          {[...Array(5)].map((_, i) => (
            <YellowStar key={i} />
          ))}
        </div>
        <Text variant="deci" color="white" as="span">
          +250 avis 5/5 sur Google
        </Text>
      </div>
    </div>
  );
}

export default function SubscriptionPage() {
  const { buildAndSetAuth } = useAuth();
  const axios = useAuthenticatedAxios();
  const nav = useNavigate();
  const { account } = useAccount();

  const [step, setStep] = useState<"formule" | "paiement">("formule");
  const [chosenNbBiens, setChosenNbBiens] = useState<number | null>(null);
  const [months, setMonths] = useState(12);
  const [locataireModalOpen, setLocataireModalOpen] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);
  const [switchError, setSwitchError] = useState<ReactNode>("");
  const { pricesByNbBiens } = useAllStripePrices();
  if (!pricesByNbBiens) return null;

  const motivation = getMotivation(account.motivation) ?? MOTIVATIONS[0];

  // Pré-rempli avec la réponse donnée à l'inscription, sinon 1 bien
  const nbBiens =
    chosenNbBiens ??
    Math.min(Math.max(Number(account.biens_a_gerer) || 1, 1), 7);

  const isAnnual = months === 12;
  const annualPrice = pricesByNbBiens[`${nbBiens}`]["12"];
  const monthlyPrice = pricesByNbBiens[`${nbBiens}`]["1"];
  const selectedPrice = isAnnual ? annualPrice : monthlyPrice;

  const annualTotalPrice = annualPrice.unit_amount / 100;
  const monthlyTotalPrice = monthlyPrice.unit_amount / 100;
  const annualTotalWithoutDiscount = monthlyTotalPrice * 12;
  const savingsPercentage = Math.round(
    ((annualTotalWithoutDiscount - annualTotalPrice) /
      annualTotalWithoutDiscount) *
      100,
  );
  const pricePerMonth = isAnnual ? annualTotalPrice / 12 : monthlyTotalPrice;
  const pricePerBien = pricePerMonth / nbBiens;

  async function finishSubscription() {
    try {
      const response = await axios.get("/signup/post-subscription");
      buildAndSetAuth(response.data);
      nav("/");
    } catch (error) {
      console.error(error);
    }
  }

  async function switchToLocataire() {
    try {
      setIsSwitching(true);
      setSwitchError("");
      const response = await axios.put("/signup/verify-tbd", {
        home: "/locataire",
      });
      buildAndSetAuth(response.data);
      nav("/locataire");
    } catch (err: unknown) {
      setSwitchError(handleAPIError(err));
    } finally {
      setIsSwitching(false);
    }
  }

  return (
    <PageLayout isTBD>
      <ModalDialog
        open={locataireModalOpen}
        onOpenChange={setLocataireModalOpen}
        header="Passer en compte locataire"
        size="sm"
      >
        <Text>
          Votre compte deviendra un espace locataire, entièrement gratuit : vous
          y retrouverez vos quittances, vos documents et vos paiements dès que
          votre bailleur vous y aura invité.
        </Text>
        <Text weight="medium">
          Vous confirmez être locataire de votre logement ?
        </Text>
        <Callout variant="danger">{switchError}</Callout>
        <div className={style.modalActions}>
          <Button
            variant="secondary"
            onClick={() => setLocataireModalOpen(false)}
          >
            Annuler
          </Button>
          <Button isLoading={isSwitching} onClick={switchToLocataire}>
            Oui, je suis locataire
          </Button>
        </div>
      </ModalDialog>

      <TemplateAuthentication
        wide
        sidePanel={<SidePanel motivation={motivation} />}
      >
        <div className={style.funnel}>
          {step === "formule" ? (
            <>
              <div>
                <Title variant="mega">Votre compte Qalimo est prêt.</Title>
                <Text>
                  Il ne vous reste plus qu'à choisir votre formule — l'essai de
                  30 jours est offert.
                </Text>
              </div>

              <div className={style.toggleGroup}>
                <button
                  type="button"
                  className={classnames(
                    style.toggleBtn,
                    !isAnnual && style.toggleBtnActive,
                  )}
                  onClick={() => setMonths(1)}
                >
                  <Text as="span">Mensuel</Text>
                </button>
                <button
                  type="button"
                  className={classnames(
                    style.toggleBtn,
                    isAnnual && style.toggleBtnActive,
                  )}
                  onClick={() => setMonths(12)}
                >
                  <Text as="span">Annuel · −{savingsPercentage} %</Text>
                </button>
              </div>

              <div className={style.biensBlock}>
                <Text>Nombre de biens à gérer</Text>
                <div>
                  <Text as="span" weight="bold" color="primary" variant="giga">
                    {nbBiens}
                  </Text>{" "}
                  <Text as="span">
                    {nbBiens < 7 ? (
                      <TextOneOrMany one="bien" many="biens" number={nbBiens} />
                    ) : (
                      "biens et plus"
                    )}
                  </Text>
                </div>
                <TextInputStandalone
                  type="range"
                  min={1}
                  max={7}
                  value={nbBiens}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setChosenNbBiens(parseInt(e.target.value))
                  }
                  className={style.rangeInput}
                />
              </div>

              <Card className={style.planCard}>
                <Status
                  color="info"
                  text={`Soit ${currencyFormatter(
                    isAnnual ? annualTotalPrice / nbBiens : pricePerBien,
                  )} par bien${isAnnual ? "/an" : "/mois"}`}
                  weight="medium"
                  className={style.planPill}
                />
                <Text
                  variant="deci"
                  weight="bold"
                  color="primary"
                  className={style.planOverline}
                >
                  FORMULE {isAnnual ? "ANNUELLE" : "MENSUELLE"}
                </Text>
                <div>
                  <Text as="span" weight="bold" variant="tera">
                    {currencyFormatter(
                      isAnnual ? annualTotalPrice : monthlyTotalPrice,
                    )}
                  </Text>{" "}
                  <Text as="span" weight="bold" color="primary" variant="deca">
                    {isAnnual ? "par an" : "par mois"}
                  </Text>
                </div>
                <Text variant="deci" color="muted" className={style.planSub}>
                  Facturation {isAnnual ? "annuelle" : "mensuelle"} · TTC
                </Text>
                {isAnnual ? (
                  <Status color="warning" text="Économie : 2 mois offerts" />
                ) : (
                  <Status color="success" text="Sans engagement" />
                )}
                <Button
                  className={style.planCta}
                  onClick={() => setStep("paiement")}
                >
                  Commencer mes 30 jours gratuits
                </Button>
                <Text
                  variant="deci"
                  color="muted"
                  className={style.planNoCharge}
                >
                  Aucun paiement aujourd'hui — annulable à tout moment.
                </Text>
                <div className={style.planDivider}></div>
                <div className={style.planFeatures}>
                  <IconAndText
                    icon={<IconCircleCheckFilled color="green" />}
                    text={
                      <Text as="span" weight="bold">
                        <TextOneOrMany
                          one={`${nbBiens} bien inclus`}
                          many={`${nbBiens} biens inclus`}
                          number={nbBiens}
                        />
                      </Text>
                    }
                  />
                  {FEATURES.map((feature) => (
                    <IconAndText
                      key={feature}
                      icon={<IconCircleCheckFilled color="green" />}
                      text={<Text as="span">{feature}</Text>}
                    />
                  ))}
                </div>
                <div className={style.reassurance}>
                  <IconAndText
                    icon={<IconCircleCheckFilled color="green" />}
                    text="Satisfait ou remboursé pendant 6 mois"
                  />
                  <IconAndText
                    icon={<IconCircleCheckFilled color="green" />}
                    text="Abonnement 100 % déductible de vos revenus locatifs"
                  />
                </div>
              </Card>

              <div className={style.tenantHint}>
                <Text variant="deci" color="muted" as="span">
                  Vous êtes locataire ? Votre espace est gratuit.
                </Text>
                <Button
                  variant="tertiary"
                  size="sm"
                  onClick={() => setLocataireModalOpen(true)}
                >
                  Basculer mon compte
                </Button>
              </div>

              <Accordion divider="gap">
                {FAQ_ITEMS.map((item) => (
                  <AccordionItem
                    key={item.title}
                    title={item.title}
                    variant="plain"
                  >
                    <Text>{item.text}</Text>
                  </AccordionItem>
                ))}
              </Accordion>
            </>
          ) : (
            <>
              <div>
                <Title variant="mega">Démarrez vos 30 jours gratuits.</Title>
                <Text>
                  Aucun prélèvement aujourd'hui. Annulable à tout moment avant
                  la fin de l'essai.
                </Text>
              </div>
              <SubscriptionCheckout
                priceId={selectedPrice.id}
                nbBiens={nbBiens}
                isAnnual={isAnnual}
                pricePerPeriod={isAnnual ? annualTotalPrice : monthlyTotalPrice}
                onBack={() => setStep("formule")}
                onSubscribed={finishSubscription}
              />
            </>
          )}
        </div>
      </TemplateAuthentication>
    </PageLayout>
  );
}
